/**
 * Semantic AUDIO CUE vocabulary — the CLOSED, versioned set of cue names a
 * creation may bind its semantic events to.
 *
 * The two sides of an adapter's `cues` table are different things and this
 * module owns exactly one of them:
 *
 *   cues: { topout: 'lose' }
 *          ^^^^^^   ^^^^^^
 *          |        THE CUE — a member of this vocabulary (or the explicit
 *          |        {@link NO_CUE}). Platform-owned, closed, versioned.
 *          the SEMANTIC EVENT — the creation's own vocabulary ('topout',
 *          'harddrop'), free-form by design: it means nothing to another game.
 *
 * Ownership split, identical to the animation (`animation.ts`) and
 * presentation-preset (`presets.ts`) vocabularies: the NAMES live here
 * (`@djgg/creations` — the builder gate, prompt interpolation, portal); the
 * SOUND each name makes lives in the reviewed, digest-pinned bank under
 * `tools/audio-bank/`, keyed by the SAME names and value-locked by that tool's
 * test. The model declares WHICH moment is which cue; it never authors timbre.
 *
 * Why a closed vocabulary: an off-list name is rejected by the builder's SOUND
 * CUES gate before publication instead of silently no-opping at runtime — the
 * same 'pickup' lesson `presets.ts` records, applied to audio. Before this
 * vocabulary existed a template shipped `cues: { miss: 'hurt' }` against a
 * runtime that had no `hurt`, and the miss was simply inaudible forever.
 *
 * Why versioned: a published game's audio semantics must not change under it.
 * Renaming, removing or re-pointing a v1 cue is an EPOCH BREAK — it changes
 * what an already-published version sounds like. Growing the vocabulary is
 * cheap (append a name, bump {@link CUE_VOCABULARY_VERSION}, add its bank
 * entries); shrinking it is a migration. `test/cueVocabulary.test.ts` pins v1
 * with a golden digest, so editing a v1 entry in place fails the suite.
 */
import { PlatformError } from '@djgg/core';

/** The only supported cue-vocabulary version. */
export const CUE_VOCABULARY_VERSION = 1 as const;

/**
 * The closed set of cue CATEGORIES the vocabulary must cover (the follow-up
 * plan's Milestone 2 list). A category is review scaffolding — it is what makes
 * "does this vocabulary cover the moments games actually have?" an answerable
 * question — and the bank's style families are authored per category, not per
 * cue, so a family sounds coherent.
 */
export const CUE_CATEGORIES = [
  'ui',
  'movement',
  'impact',
  'attack',
  'damage',
  'score',
  'collect',
  'countdown',
  'win',
  'lose',
  'service',
  'ambience',
] as const;

/** One member of {@link CUE_CATEGORIES}. */
export type CueCategory = (typeof CUE_CATEGORIES)[number];

/**
 * Vocabulary v1: cue name → its category. FROZEN — see the epoch-break rule in
 * the module docblock.
 *
 * The nine names the platform's procedural bank already played (`click`,
 * `success`, `fail`, `countdown`, `score`, `whoosh`, `shot`, `impact`,
 * `reload`) are carried verbatim so every live adapter and every maintained
 * template is valid under v1 without an edit.
 */
export const CUE_VOCABULARY_V1 = Object.freeze({
  /** A button, a menu row, a confirmed selection. */
  click: 'ui',
  /** A screen/menu transition. */
  whoosh: 'ui',
  jump: 'movement',
  land: 'movement',
  dash: 'movement',
  /** A hard contact that is not damage — a wall, a paddle, a block landing. */
  impact: 'impact',
  bounce: 'impact',
  shot: 'attack',
  swing: 'attack',
  reload: 'attack',
  /** The player (or a rival) TAKES damage. */
  hurt: 'damage',
  /** Something is destroyed outright. */
  destroy: 'damage',
  score: 'score',
  collect: 'collect',
  powerup: 'collect',
  countdown: 'countdown',
  timeup: 'countdown',
  win: 'win',
  /** Positive confirmation short of winning — a cleared line, a solved word. */
  success: 'win',
  lose: 'lose',
  /** Negative confirmation short of losing — a wrong answer, a missed catch. */
  fail: 'lose',
  /** Service games (an order taken, a dish served, a customer seated). */
  serve: 'service',
  deliver: 'service',
  /** A continuous atmospheric bed, not a one-shot. */
  ambience: 'ambience',
} as const satisfies Readonly<Record<string, CueCategory>>);

/** One member of the v1 cue vocabulary. */
export type CueName = keyof typeof CUE_VOCABULARY_V1;

/** Every v1 cue name, sorted — the stable order the bank and its digest use. */
export const CUE_NAMES: readonly CueName[] =
  Object.freeze((Object.keys(CUE_VOCABULARY_V1) as CueName[]).sort());

/**
 * The explicit "this moment is deliberately silent" binding. It is a BINDING,
 * not a cue: a declared semantic event must resolve to a cue or to this, so
 * that "no sound here" is a reviewed decision in the source rather than an
 * omission indistinguishable from a typo.
 */
export const NO_CUE = 'none' as const;

/** A semantic event's binding: a v1 cue, or explicit silence. */
export type CueBinding = CueName | typeof NO_CUE;

export function isCueName(value: unknown): value is CueName {
  return typeof value === 'string' && Object.hasOwn(CUE_VOCABULARY_V1, value);
}

export function isCueBinding(value: unknown): value is CueBinding {
  return value === NO_CUE || isCueName(value);
}

/**
 * Semantic EVENT name rule — the creation's own side of the table. Same shape
 * the persisted {@link import('./soundCues.js').SoundCueCatalogV1} accepts, so
 * a name that passes the gate is a name the creator's binding UI can show.
 */
const EVENT_NAME = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const RESERVED_EVENT_NAMES: readonly string[] = ['__proto__', 'prototype', 'constructor'];

/** Ceiling on one creation's semantic-event table; matches the persisted
 *  catalog's own `names` bound so a passing build is always representable. */
export const MAX_SEMANTIC_EVENTS = 64;

export function isSemanticEventName(value: unknown): value is string {
  return typeof value === 'string' && EVENT_NAME.test(value) && !RESERVED_EVENT_NAMES.includes(value);
}

/**
 * One creation's reviewed event → binding table, rebuilt and sorted.
 *
 * The creator-facing failure wording is deliberate: it names the offending
 * value, the closed alternative, and `none`, because this text is also the
 * repair fuel the builder hands back to the model.
 */
export function validateCueBindingsV1(value: unknown): Readonly<Record<string, CueBinding>> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new PlatformError('validation', 'cueBindings must be an object of semantic event → cue');
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length > MAX_SEMANTIC_EVENTS) {
    throw new PlatformError('validation', `cueBindings declares ${entries.length} semantic events; at most ${MAX_SEMANTIC_EVENTS} are allowed`);
  }
  const bindings: Record<string, CueBinding> = {};
  for (const [event, binding] of entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))) {
    if (!isSemanticEventName(event)) {
      throw new PlatformError('validation', `cueBindings key ${JSON.stringify(event)} is not a usable semantic event name`);
    }
    if (!isCueBinding(binding)) {
      throw new PlatformError(
        'validation',
        `cueBindings.${event} is bound to ${JSON.stringify(binding)}, which is not a cue in vocabulary v${CUE_VOCABULARY_VERSION}. ` +
          `Use one of: ${CUE_NAMES.join(', ')} — or '${NO_CUE}' to declare this moment deliberately silent`,
      );
    }
    bindings[event] = binding;
  }
  return Object.freeze(bindings);
}
